import { Box, Typography } from '@mui/material';
import React from 'react';

import { fieldHasValue, renderField } from './InteractionFieldRenderers';
import { SectionLabel } from './SectionLabel';
import { FormContainerItem, FormFieldItem, FormFieldType, FormObject } from '../../../../@types';
import { ServiceObject } from '../../../../@types/service.type';
import { RadioGroup } from '../../../../components/atoms/RadioGroup';
import { DESKTOP_SELF_LABELLING_FIELD_TYPES } from '../../../../components/organisms/InteractionFields';
import { DECISION_PROMPT_OPTIONS } from '../../../../constants/form-builder';

export interface InteractionFormProps {
  form: FormObject;
  onChange: (form: FormObject) => void;
  serviceId?: number;
  /**
   * Required by the field types in `NEEDS_SERVICE_CONTEXT`. Callers that only
   * have an id should check `formNeedsServiceContext` and fetch when it is true.
   */
  service?: ServiceObject;
}

const NATIVELY_SUPPORTED: ReadonlySet<FormFieldType> = new Set([
  FormFieldType.Text,
  FormFieldType.RichTextbox,
  FormFieldType.Date,
  FormFieldType.Checkbox,
  FormFieldType.Dropdown,
  FormFieldType.Radio,
  FormFieldType.Label,
  FormFieldType.Presentation,
  FormFieldType.Photo,
  FormFieldType.File,
  FormFieldType.LabourTime,
  FormFieldType.EstimatedTimeOfArrival,
  FormFieldType.MerchantDamageTemplate,
  FormFieldType.SetParameter,
  FormFieldType.SetParameterNoUI,
  FormFieldType.ActionTrigger,
  FormFieldType.GetParameter,
  FormFieldType.Block,
  FormFieldType.FaultSelection,
  FormFieldType.SolutionAction,
  FormFieldType.PartsRequest,
  FormFieldType.Signature,
  FormFieldType.GetGeoLocation,
]);

/**
 * Field types that need the whole `ServiceObject`, not just its id, because they
 * query against the service's contract/location. A caller that can only supply
 * an id must fetch the service for these.
 */
// The Yes/No values come from the shared constant so both renderers agree on what
// gets written into `questionData`.
const [YES_OPTION, NO_OPTION] = DECISION_PROMPT_OPTIONS;
const YES = YES_OPTION.value;
const NO = NO_OPTION.value;

const NEEDS_SERVICE_CONTEXT: ReadonlySet<FormFieldType> = new Set([
  FormFieldType.FaultSelection,
  FormFieldType.SolutionAction,
  FormFieldType.PartsRequest,
]);

/** True if any field in the form needs the full service object to render. */
export const formNeedsServiceContext = (form: FormObject): boolean =>
  form.containers.some((c) => c.fields.some((f) => NEEDS_SERVICE_CONTEXT.has(f.type)));

/**
 * True if every field in the form has a renderer in the new style. When false,
 * callers should fall back to the legacy FormInterceptor for the whole form so
 * the four remaining legacy-only types (ServiceCreation, DecisionPrompt,
 * ModifyStructure, ServiceSearch) keep working — at the cost of the legacy visual
 * language for that single form.
 *
 * Prefer `canRenderFormNatively` at call sites: field support alone is not
 * enough to route a form here safely.
 */
export const isFormNativelyRenderable = (form: FormObject): boolean =>
  form.containers.every((c) => c.fields.every((f) => NATIVELY_SUPPORTED.has(f.type)));

/**
 * The routing gate: true only when this renderer can reproduce *everything*
 * `FormGenerator` would do for the form. Field-type support is necessary but not
 * sufficient — the legacy renderer implements one structural behaviour this
 * component still does not:
 *
 * - **Sub-conditions.** `FormGenerator` splices `thenContainer`/`elseContainer`
 *   fields into later containers when a Dropdown/Radio/Checkbox changes
 *   (`FormGenerator.tsx:98-165`). A form routed here would render once and then
 *   stop reacting, silently losing its dynamic fields.
 *
 * **Decision prompts are supported** and no longer force a fallback: a container
 * with `isDecisionPrompt` renders its question and gates its fields on a Yes,
 * writing `questionData.isYes`/`hasValue` in the same shape the legacy
 * `DecisionPromptFormFragment` produces. They were the single most common reason a
 * real template dropped to the legacy renderer.
 *
 * The remaining clause is deliberately *structural* — it depends only on the
 * template shape, never on user input — so a form cannot change renderer mid-edit
 * and remount under the user. In particular `form.byPassedFormFields` is NOT
 * consulted: `validateInteractionForms` produces it during validation
 * (`utils/interactions.ts:147`), so it changes as the user works.
 */
export const canRenderFormNatively = (form: FormObject): boolean =>
  isFormNativelyRenderable(form) && form.containers.every((c) => !c.subConditions?.length);

const updateFieldInForm = (
  form: FormObject,
  containerIdx: number,
  fieldIdx: number,
  next: FormFieldItem
): FormObject => {
  const containers: FormContainerItem[] = form.containers.map((container, ci) => {
    if (ci !== containerIdx) return container;
    const fields = container.fields.map((field, fi) => (fi === fieldIdx ? next : field));
    return { ...container, fields };
  });
  return { ...form, containers };
};

const updateContainerInForm = (
  form: FormObject,
  containerIdx: number,
  next: FormContainerItem
): FormObject => ({
  ...form,
  containers: form.containers.map((container, ci) => (ci === containerIdx ? next : container)),
});

/**
 * Record the answer to a decision prompt in the shape the legacy renderer produces,
 * so the submitted payload is identical either way — `isYes` drives whether the
 * container's fields count, and `hasValue` marks the question as answered.
 */
const answerDecisionPrompt = (container: FormContainerItem, isYes: boolean): FormContainerItem => ({
  ...container,
  questionData: { ...container.questionData, isYes, hasValue: true },
});

/** The Yes/No question that gates a decision-prompt container's fields. */
const DecisionPrompt: React.FC<{
  container: FormContainerItem;
  onAnswer: (isYes: boolean) => void;
}> = ({ container, onAnswer }) => {
  const answered = container.questionData?.hasValue;
  const value = answered ? (container.questionData?.isYes ? YES : NO) : '';

  return (
    <Box>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>
        {container.questionData?.questionText}
      </Typography>
      <RadioGroup
        id={`decision-prompt-${container.id}`}
        title=""
        options={DECISION_PROMPT_OPTIONS}
        value={value}
        onChange={(next) => onAnswer(next === YES)}
      />
    </Box>
  );
};

interface ContainerFieldsProps {
  container: FormContainerItem;
  onFieldChange: (fieldIdx: number, next: FormFieldItem) => void;
  serviceId?: number;
  service?: ServiceObject;
}

/** The fields of one container, each with the right amount of label around it. */
const ContainerFields: React.FC<ContainerFieldsProps> = ({
  container,
  onFieldChange,
  serviceId,
  service,
}) => (
  <>
    {container.fields.map((field, fi) => {
      const onChange = (next: FormFieldItem) => onFieldChange(fi, next);

      // Checkbox carries its own label so we skip the duplicate SectionLabel.
      if (field.type === FormFieldType.Checkbox) {
        return (
          <Box key={field.id}>
            {renderField(field, onChange, serviceId, service)}
            {field.isRequired && !fieldHasValue(field) && (
              <Typography sx={{ fontSize: '11px', color: 'error.main', ml: 4 }}>
                Required
              </Typography>
            )}
          </Box>
        );
      }

      // Controls that draw their own label — and the types that render nothing —
      // must not get a second label from SectionLabel.
      if (DESKTOP_SELF_LABELLING_FIELD_TYPES.has(field.type)) {
        return <Box key={field.id}>{renderField(field, onChange, serviceId, service)}</Box>;
      }

      return (
        <SectionLabel key={field.id} label={field.title} required={field.isRequired}>
          {renderField(field, onChange, serviceId, service)}
        </SectionLabel>
      );
    })}
  </>
);

export const InteractionForm: React.FC<InteractionFormProps> = ({
  form,
  onChange,
  serviceId,
  service,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {form.containers.map((container, ci) => {
      // A decision-prompt container asks a Yes/No question first and collects its
      // fields only on Yes — matching FormGenerator.tsx:254-257. Without this, any
      // form containing one fell back to the legacy renderer wholesale, which is what
      // put the old styling back on the busiest templates.
      const gated = !!container.isDecisionPrompt && !container.questionData?.isYes;

      return (
        <Box key={container.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {container.isDecisionPrompt && (
            <DecisionPrompt
              container={container}
              onAnswer={(isYes) =>
                onChange(updateContainerInForm(form, ci, answerDecisionPrompt(container, isYes)))
              }
            />
          )}
          {!gated && (
            <ContainerFields
              container={container}
              onFieldChange={(fi, next) => onChange(updateFieldInForm(form, ci, fi, next))}
              serviceId={serviceId}
              service={service}
            />
          )}
        </Box>
      );
    })}
  </Box>
);
